// Receipt designs.
//
// Three presets live here rather than in the database so every organization has
// something that prints the moment the Printer module is switched on — no rows
// to seed, and nothing a user can delete and leave themselves unable to print.
// A shop's own designs are rows in `bill_templates`, created by duplicating one
// of these.

/**
 * Characters that fit across a roll. The receipt builder used a hard-coded 48
 * regardless of paper, so every 58mm printer wrapped its lines and produced an
 * unreadable bill. Sizes are the industry standard widths, with the character
 * counts ESC/POS Font A gives on each.
 */
export const CHARS_PER_LINE = {
  58: 32, 76: 40, 80: 48, 112: 69,   // thermal and dot-matrix rolls
  148: 56, 210: 80, 216: 80,         // A5, A4, US Letter
};

/**
 * The paper sizes a design can be set to.
 *
 * Rolls print continuously — the bill is as long as it needs to be, so they
 * have no height. Sheets do, and a page break matters on them, so `heightMm`
 * is what tells the browser to ask for a real A4 rather than a 210mm-wide
 * ribbon. `group` is only for the picker.
 */
export const PAPER_SIZES = [
  { mm: 58, label: '58 mm', note: 'Small thermal roll — handhelds, small counters', group: 'Thermal roll' },
  { mm: 76, label: '76 mm', note: 'Dot-matrix roll — kitchen and impact printers', group: 'Thermal roll' },
  { mm: 80, label: '80 mm', note: 'Standard thermal roll — most counter printers', group: 'Thermal roll' },
  { mm: 112, label: '112 mm', note: 'Wide dot-matrix — reports and long invoices', group: 'Thermal roll' },
  { mm: 148, heightMm: 210, label: 'A5', note: 'Half sheet — short invoices on a normal printer', group: 'Sheet' },
  { mm: 210, heightMm: 297, label: 'A4', note: 'Full sheet — the usual office invoice', group: 'Sheet' },
  { mm: 216, heightMm: 279, label: 'Letter', note: 'US Letter sheet', group: 'Sheet' },
];

export const findPaperSize = (mm) => PAPER_SIZES.find((p) => p.mm === mm) || null;

/**
 * How the item table is divided across the paper.
 *
 * These were two ternaries repeated in the printer and in the preview, which is
 * exactly the sort of thing that drifts apart: the printer's columns were fixed
 * at 28/6/14 and overflowed a 58mm line before a single character of the item
 * name was printed. One table, read by both.
 */
const COLUMNS = {
  32: { qty: 4, amt: 9 },
  40: { qty: 5, amt: 12 },
  48: { qty: 6, amt: 14 },
  56: { qty: 6, amt: 14 },
  69: { qty: 7, amt: 16 },
  80: { qty: 8, amt: 18 },
};

/**
 * Columns for a line of any width, not only the sizes shipped here.
 *
 * The table covers the standard papers exactly. A shop that has told us its
 * printer fits 42 or 64 characters gets a proportional split instead of a
 * lookup miss falling back to 48 — which would push its amounts off the roll.
 */
const columnsFor = (width) => {
  if (COLUMNS[width]) return COLUMNS[width];
  return {
    qty: Math.max(4, Math.min(8, Math.round(width * 0.09))),
    amt: Math.max(9, Math.min(18, Math.round(width * 0.22))),
  };
};

/**
 * How many characters fit on a line, and how the item table divides them.
 *
 * `charsPerLine` overrides the width worked out from the paper. Characters per
 * line cannot actually be derived from millimetres: it is the print head's dot
 * count divided by the font's dot width, and printers of the same paper width
 * differ on both — a 58mm head is usually 384 dots, giving 384/12 = 32 in ESC/POS
 * Font A, but 512-dot 80mm units and Font B at 9 dots wide are both common. The
 * defaults here are right for most printers; the override is for the rest, and
 * is found by printing one test bill and counting.
 */
export const receiptColumns = (paperWidth, charsPerLine) => {
  const override = Number(charsPerLine);
  const width = Number.isFinite(override) && override >= 20 && override <= 200
    ? Math.round(override)
    : (CHARS_PER_LINE[paperWidth] || CHARS_PER_LINE[80]);
  const { qty, amt } = columnsFor(width);
  return { width, qtyCol: qty, amtCol: amt, nameCol: width - qty - amt };
};

export const DEFAULT_SETTINGS = {
  paperWidth: 80,          // mm — see PAPER_SIZES
  charsPerLine: null,      // empty = work it out from the paper; see receiptColumns
  title: 'RECEIPT',        // printed under the shop name
  showBranch: true,        // which shop or counter issued it
  showAddress: true,
  showPhone: true,
  showGstin: false,
  showBillNo: true,
  showDate: true,
  showTime: false,
  showCustomer: true,
  showPaymentMode: true,
  // Off makes the slip a kitchen ticket or a packing list: items and quantities
  // with no money on them at all, totals included.
  showPrices: true,
  showTaxBreakdown: true,
  showDeliveryAddress: false,
  footerLine1: 'THANK YOU FOR YOUR PURCHASE!',
  footerLine2: 'Please visit again.',
  copies: 1,
};

/**
 * The built-in designs. `id` is prefixed so a preset can never be confused with
 * a saved row, and presets are read-only — editing one means duplicating it.
 */
export const BILL_PRESETS = [
  {
    id: 'preset:standard',
    name: 'Standard Receipt',
    description: 'Everyday sales. Shop details, items, total.',
    settings: { ...DEFAULT_SETTINGS },
  },
  {
    id: 'preset:gst',
    name: 'GST Tax Invoice',
    description: 'For business customers. Shows GSTIN and the tax breakdown.',
    settings: {
      ...DEFAULT_SETTINGS,
      title: 'TAX INVOICE',
      showGstin: true,
      showTaxBreakdown: true,
      footerLine1: 'This is a computer generated invoice.',
      footerLine2: '',
    },
  },
  {
    id: 'preset:compact58',
    name: 'Compact (58mm)',
    description: 'Short slip for 58mm paper rolls.',
    settings: {
      ...DEFAULT_SETTINGS,
      paperWidth: 58,
      showAddress: false,
      showPaymentMode: false,
      showTaxBreakdown: false,
      footerLine1: 'THANK YOU',
      footerLine2: '',
    },
  },
  {
    id: 'preset:kot',
    name: 'Kitchen Order Ticket',
    description: 'For the kitchen. Items and quantities, no prices.',
    settings: {
      ...DEFAULT_SETTINGS,
      title: 'KITCHEN ORDER',
      showBranch: true,
      showAddress: false,
      showPhone: false,
      showGstin: false,
      showTime: true,
      showPaymentMode: false,
      showPrices: false,
      showTaxBreakdown: false,
      footerLine1: '',
      footerLine2: '',
    },
  },
  {
    id: 'preset:delivery',
    name: 'Delivery Slip',
    description: 'Goes with the order. Address and items, no tax lines.',
    settings: {
      ...DEFAULT_SETTINGS,
      title: 'DELIVERY SLIP',
      showGstin: false,
      showTime: true,
      showTaxBreakdown: false,
      showDeliveryAddress: true,
      footerLine1: 'Please check the items on delivery.',
      footerLine2: '',
    },
  },
  {
    id: 'preset:estimate',
    name: 'Estimate / Quotation',
    description: 'A price quote. Says plainly it is not a tax invoice.',
    settings: {
      ...DEFAULT_SETTINGS,
      title: 'ESTIMATE',
      showGstin: false,
      showPaymentMode: false,
      footerLine1: 'This is not a tax invoice.',
      footerLine2: 'Prices valid for 7 days.',
    },
  },
  {
    id: 'preset:detailed',
    name: 'Detailed Tax Invoice',
    description: 'Wide 112mm paper. Everything on one line, nothing shortened.',
    settings: {
      ...DEFAULT_SETTINGS,
      paperWidth: 112,
      title: 'TAX INVOICE',
      showGstin: true,
      showTime: true,
      footerLine1: 'This is a computer generated invoice.',
      footerLine2: '',
    },
  },
  {
    id: 'preset:token',
    name: 'Token Slip',
    description: 'Queue number for the customer to wait with. 58mm.',
    settings: {
      ...DEFAULT_SETTINGS,
      paperWidth: 58,
      title: 'TOKEN',
      showAddress: false,
      showPhone: false,
      showGstin: false,
      showTime: true,
      showPaymentMode: false,
      showPrices: false,
      showTaxBreakdown: false,
      footerLine1: 'Please wait for your number.',
      footerLine2: '',
    },
  },
];

export const findPreset = (id) => BILL_PRESETS.find((p) => p.id === id) || null;

export const isPresetId = (id) => typeof id === 'string' && id.startsWith('preset:');

/** The design used when a shop has never chosen one. */
export const DEFAULT_TEMPLATE_ID = 'preset:standard';

/**
 * Resolve whichever design applies, given the presets, the shop's own saved
 * designs, and the id a branch or organization selected. Falls back to the
 * standard preset rather than failing, so a deleted or unknown id can never
 * leave a till unable to print.
 */
export const resolveTemplateSettings = (templateId, customTemplates = []) => {
  const preset = findPreset(templateId);
  if (preset) return { ...DEFAULT_SETTINGS, ...preset.settings };

  const own = customTemplates.find((t) => t.id === templateId);
  if (own) return { ...DEFAULT_SETTINGS, ...(own.settings || {}) };

  return { ...DEFAULT_SETTINGS, ...findPreset(DEFAULT_TEMPLATE_ID).settings };
};
